import React, { useState } from "react";
import { ModalProps } from "../../../Shared/types/ModalProps";
import { GenericButton } from "../../../Shared/components/GenericButton";
import {
  GenericFileInput,
  GenericInput,
  GenericMultipleFileInput,
} from "../../../Shared/components/GenericInput";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { CreateCompanyInput } from "../models/CreateCompanyInput";
import { CompanyService } from "../services/company.service";
import { Toast } from "../../../Shared/components/Toast";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { ICompany } from "../models/Company";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { GenericSelectInput } from "../../../Shared/components/GenericSelectInput";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FileModel } from "../../../Shared/dto/create-file-input";
import { useTranslation } from "react-i18next";

type CompanyFormProps = ModalProps & {
  company?: ICompany;
};

export const CompanyForm: React.FC<CompanyFormProps> = ({
  onClose,
  company,
}) => {
  const { t } = useTranslation();
  const message = t('loading_message');
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isLoading, setIsLoading] = useState(false);

  const [country, setCountry] = useState(company?.country || "");
  const [countryError, setCountryError] = useState("");
  const [name, setName] = useState(company?.name || "");
  const [nameError, setNameError] = useState("");
  const [description, setDescription] = useState(company?.description || "");
  const [descriptionError, setDescriptionError] = useState("");
  const [address, setAddress] = useState(company?.address || "");
  const [taxPayerId, setTaxPayerId] = useState<FileModel | null>(
    UtilService.attachmentsToFileModel([company?.taxPayerId])?.[0] || null
  );
  const [taxPayerIdError, setTaxPayerIdError] = useState("");
  const [license, setLicense] = useState<FileModel | null>(
    UtilService.attachmentsToFileModel([company?.license])?.[0] || null
  );
  const [licenseError, setLicenseError] = useState("");
  const [registration, setRegistration] = useState<FileModel | null>(
    UtilService.attachmentsToFileModel([company?.registration])?.[0] || null
  );
  const [registrationError, setRegistrationError] = useState("");
  const [attachments, setAttachments] = useState<FileModel[]>(
    UtilService.attachmentsToFileModel(company?.attachments) || []
  );
  const [attachmentError, setAttachmentError] = useState("");

  const onCountry = (val: string) => {
    setCountry(val);
    setCountryError(val.trim() === "" ? t('select_country') : "");
  };

  const onName = (val: string) => {
    setName(val);
    setNameError(val.trim() === "" ? t('name_required') : "");
  };

  const onAddress = (val: string) => setAddress(val);

  const onDescription = (val: string) => {
    setDescription(val);
    setDescriptionError(val.trim() === "" ? t('description_required') : "");
  };

  const onTaxPayerIdAttachment = async (file: File) => {
    const response = await UtilService.onAttachFile(file, setTaxPayerIdError);
    if (response) {
      setTaxPayerId(response);
    }
  };

  const onLicenseAttachment = async (file: File) => {
    const response = await UtilService.onAttachFile(file, setLicenseError);
    if (response) {
      setLicense(response);
    }
  };

  const onRegistrationAttachment = async (file: File) => {
    const response = await UtilService.onAttachFile(file, setRegistrationError);
    if (response) {
      setRegistration(response);
    }
  };

  const onFileAttachments = async (files: File[]) => {
    let attachmentFiles = await Promise.all(
      files.map((file) => UtilService.onAttachFile(file, setAttachmentError))
    );
    if (attachmentFiles?.length > 0) {
      setAttachments(
        attachmentFiles.filter((file): file is FileModel => file !== undefined)
      );
    }
  };

  const resetForm = () => {
    setCountry("");
    setCountryError("");

    setName("");
    setNameError("");

    setDescription("");
    setDescriptionError("");

    setAddress("");

    setTaxPayerId(null);
    setTaxPayerIdError("");

    setLicense(null);
    setLicenseError("");

    setRegistration(null);
    setRegistrationError("");

    setAttachments([]);
    setAttachmentError("");
  };

  const isNameValid = name.trim() !== "";
  const isDescriptionValid = description.trim() !== "";
  const isAddressValid = address.trim() !== "";

  const isFormValid =
    isNameValid &&
    isDescriptionValid &&
    isAddressValid &&
    !nameError &&
    !descriptionError &&
    !taxPayerIdError &&
    !licenseError;

  const createAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const createCompanyInput = new CreateCompanyInput();
    if (company?.id) {
      createCompanyInput.id = company.id;
    }
    createCompanyInput.name = name.trim();
    createCompanyInput.country = country.trim();
    createCompanyInput.address = address.trim();
    createCompanyInput.description = description.trim();
    createCompanyInput.taxPayerId = taxPayerId ?? undefined;
    createCompanyInput.license = license ?? undefined;
    createCompanyInput.registration = registration ?? undefined;
    createCompanyInput.attachments = attachments;
    try {
      await CompanyService.create(createCompanyInput);
      resetForm();
      onClose(true);
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

const countryOptions = t('country_options', { returnObjects: true }) as { label: string, value: string }[];

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-lg p-5 mt-10 mb-10 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center pb-3 border-gray-300 dark:border-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {company?.id ? t('update_company') : t('create_company')}
            </h3>
            <button
              onClick={() => onClose(true)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form className="mt-4" onSubmit={createAction}>
            <GenericSelectInput
              label={t('country_label')}
              options={countryOptions}
              value={country}
              onChange={onCountry}
              required
              placeholder={t('select_country')}
              error={countryError}
            />
            <GenericInput
              label={t('company_name_label')}
              value={name}
              onChange={onName}
              type="text"
              error={nameError}
              placeholder={t('company_name_hint')}
              required
            />

            <GenericInput
              label={t('company_address_label')}
              value={address}
              onChange={onAddress}
              type="text"
              placeholder={t('company_address_hint')}
              required
            />

            <GenericInput
              label={t('company_description_label')}
              value={description}
              onChange={onDescription}
              type="text"
              error={descriptionError}
              placeholder={t('company_description_hint')}
              required
            />

            <div className="grid gap-6 md:grid-cols-2">
              <GenericFileInput
                containerClassName=""
                label={t('tax_payer_id_label')}
                onChange={onTaxPayerIdAttachment}
                error={taxPayerIdError}
                placeholder={t('tax_payer_id_hint')}
              />

              <GenericFileInput
                containerClassName=""
                label={t('license_label')}
                onChange={onLicenseAttachment}
                error={licenseError}
                placeholder={t('license_hint')}
              />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <GenericFileInput
                label={t('company_registration_label')}
                onChange={onRegistrationAttachment}
                error={registrationError}
                placeholder={t('company_registration_hint')}
                required={false}
              />

              <GenericMultipleFileInput
                label={t('other_attachments_label')}
                onChange={onFileAttachments}
                error={attachmentError}
                placeholder={t('other_attachments_hint')}
                required={false}
              />
            </div>
            <div className="mx-5">
              <GenericButton type="submit" disabled={!isFormValid}>
                {company?.id ? t('update_company') : t('create_company')}
              </GenericButton>
            </div>
          </form>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
